/**
 * The REAL session loader.
 *
 * This replaces the development stub that used to seed a fake user. The stub is
 * gone: there is no code path in which the application believes a session exists
 * when the server has not said so.
 *
 * WHAT IT PUBLISHES
 * -----------------
 * One read of `GET /api/v1/identity/session` produces BOTH halves of the session:
 *
 *   - the SESSION (`src/app/session`) — the actor, their permissions on the
 *     selected tenant, their platform grants, and the session's expiry.
 *   - the WORKSPACE SCOPE (`src/app/workspace`) — the RESOLVED tenant the session
 *     was read in.
 *
 * They are published together on purpose. A session without its scope would leave
 * the shell rendering a tenant's name it does not have, and a scope without its
 * session would leave a signed-out user inside a tenant's screens.
 *
 * THE CAPABILITIES
 * ----------------
 * `capabilities` are the TENANT's entitlements, read from the server. They are not
 * derived from permissions and not derived from the tenant slug: an entitlement is a
 * thing the tenant has been given, and only the server knows which.
 *
 * A failure to read entitlements does NOT fail the session. A tenant with no
 * entitlements is a tenant with an empty list, and a transport fault there must not
 * sign the user out — it must simply leave the capability-gated destinations out.
 */
import { http } from '@/api/client/http';
import { EntitlementsResponseSchema } from '@/api/schemas/foundation';
import { authApi } from '@/modules/auth/services/api';
import { configureSessionLoader } from '@/app/session/session-state';
import { setWorkspaceScope } from '@/app/workspace/store';
import type { SessionUser } from '@/app/session/types';

/**
 * The capability ids the tenant holds.
 *
 * Only ACTIVE and USABLE entitlements count. A revoked one is excluded outright, and
 * an unusable one (granted but not yet effective) is excluded too — the admin must
 * not offer a screen the tenant cannot use yet.
 */
function usableCapabilities(capabilities: readonly string[]): readonly string[] {
  return [...new Set(capabilities)].sort();
}

/**
 * Read the tenant's entitlements.
 *
 * Returns an empty list when the read fails. The failure is a missing capability,
 * not a broken session: the user stays signed in and the capability-gated
 * destinations stay hidden.
 */
async function loadCapabilities(): Promise<readonly string[]> {
  try {
    const response = await http.get('/foundation/entitlements', EntitlementsResponseSchema);
    return usableCapabilities(
      response.data.filter((entitlement) => entitlement.usable && entitlement.state === 'active').map((e) => e.capability),
    );
  } catch {
    return [];
  }
}

/**
 * Install the real loader.
 *
 * Called once, before the router renders its first match — the guards read the
 * session synchronously, so a loader that resolved after first paint would let one
 * frame of the wrong screen through.
 */
export async function installRealSession(): Promise<void> {
  configureSessionLoader(async () => {
    const projection = await authApi.loadSession();

    if (projection === null) {
      // Nobody is signed in. The workspace is cleared too: leaving a stale scope
      // behind would let a guard read a tenant the session no longer has.
      setWorkspaceScope({ status: 'error', message: 'Not signed in' }, null);
      return { user: null, expiresAt: null };
    }

    const capabilities = await loadCapabilities();

    const user: SessionUser = {
      id: projection.data.user.id,
      displayName: projection.data.user.displayName,
      // The TENANT-scoped axis.
      permissions: projection.data.user.permissions,
      // The PLATFORM-scoped axis. Kept beside the tenant one, never merged into it.
      platformPermissions: projection.data.user.platformPermissions,
      capabilities,
      isPlatformOperator: projection.data.user.isPlatformOperator,
    };

    // The RESOLVED tenant — the one the server read the session in, never the
    // selector the client asked for.
    const scope = {
      tenant: {
        id: projection.data.tenant.id,
        slug: projection.data.tenant.slug,
      },
      capabilities,
      permissions: projection.data.user.permissions,
    };

    setWorkspaceScope({ status: 'resolved', scope }, scope);

    const expiresAt = Date.parse(projection.data.session.expiresAt);

    return {
      user,
      expiresAt: Number.isNaN(expiresAt) ? null : expiresAt,
    };
  });
}
